// src/components/Exercise/ExerciseCommonFields.tsx
import React, { useState, useEffect } from 'react';
import { Box, TextField, FormControlLabel, Checkbox } from '@mui/material';

interface ExerciseCommonFieldsProps {
  instructions: string;
  additionalInfo: string;
  order: number;
  maxScore: number;
  requiresManualChecking?: boolean;
  allowReset?: boolean;
  showCorrectAnswers?: boolean;
  onChange: (field: string, value: any) => void;
}

const ExerciseCommonFields: React.FC<ExerciseCommonFieldsProps> = ({ instructions, additionalInfo, order, maxScore, requiresManualChecking = false, allowReset = true, showCorrectAnswers = true, onChange }) => {
  // Local state for the checkboxes to ensure they work
  const [localChecked, setLocalChecked] = useState(requiresManualChecking);
  const [localAllowReset, setLocalAllowReset] = useState(allowReset);
  const [localShowCorrectAnswers, setLocalShowCorrectAnswers] = useState(showCorrectAnswers);

  // Sync local state with parent props
  useEffect(() => {
    setLocalChecked(requiresManualChecking);
  }, [requiresManualChecking]);

  useEffect(() => {
    setLocalAllowReset(allowReset);
  }, [allowReset]);

  useEffect(() => {
    setLocalShowCorrectAnswers(showCorrectAnswers);
  }, [showCorrectAnswers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.name, e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    const fieldName = e.target.name;
    
    if (fieldName === 'requiresManualChecking') {
      setLocalChecked(newValue);
    } else if (fieldName === 'allowReset') {
      setLocalAllowReset(newValue);
    } else if (fieldName === 'showCorrectAnswers') {
      setLocalShowCorrectAnswers(newValue);
    }
    
    onChange(fieldName, newValue); // Notify parent
  };

  return (
    <Box mb={4}>
      <TextField
        label="Järjestys"
        name="order"
        type="number"
        value={order}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 3 }}
      />
      <TextField
        label="Ohjeet"
        name="instructions"
        value={instructions}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={2}
        sx={{ mb: 3 }}
      />
      <TextField
        label="Lisätiedot"
        name="additionalInfo"
        value={additionalInfo}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={2}
        sx={{ mb: 3 }}
      />      
      <TextField
        label="Maksimipisteet"
        name="maxScore"
        type="number"
        value={maxScore}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 3 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            name="requiresManualChecking"
            checked={localChecked}
            onChange={handleCheckboxChange}
          />
        }
        label="Tarkista manuaalisesti (ohittaa automaattisen tarkistuksen)"
      />
      <FormControlLabel
        control={
          <Checkbox
            name="allowReset"
            checked={localAllowReset}
            onChange={handleCheckboxChange}
          />
        }
        label="Salli opiskelijan nollata harjoitus"
      />
      <FormControlLabel
        control={
          <Checkbox
            name="showCorrectAnswers"
            checked={localShowCorrectAnswers}
            onChange={handleCheckboxChange}
          />
        }
        label="Näytä oikeat vastaukset opiskelijalle"
      />
    </Box>
  );
};

export default ExerciseCommonFields;
